/**
 * Template Constraints Configuration
 * Defines metadata for each template to ensure dynamic content fits properly
 */

/**
 * @typedef {Object} TextZoneConstraints
 * @property {number} maxLines - Maximum number of lines
 * @property {number} maxCharsPerLine - Approximate max characters per line
 * @property {Object} fontSizeRange - Font size range
 * @property {number} fontSizeRange.min - Minimum font size (px)
 * @property {number} fontSizeRange.max - Maximum font size (px)
 * @property {Object} position - Bounding box position
 * @property {number} position.x - X coordinate
 * @property {number} position.y - Y coordinate
 * @property {number} position.width - Width
 * @property {number} position.height - Height
 * @property {'left' | 'center' | 'right'} alignment - Text alignment
 * @property {string[]} [allowedStyles] - Allowed text styles (e.g., ['bold', 'uppercase'])
 */

/**
 * @typedef {Object} TemplateConstraints
 * @property {string} id - Template ID
 * @property {string} name - Template name
 * @property {'SQUARE' | 'VERTICAL' | 'HORIZONTAL'} size - Template size
 * @property {Object} textZones - Text zone constraints
 * @property {TextZoneConstraints} textZones.heading - Heading constraints
 * @property {TextZoneConstraints} [textZones.subheading] - Subheading constraints
 * @property {TextZoneConstraints} [textZones.description] - Description constraints
 * @property {TextZoneConstraints} [textZones.cta] - CTA constraints
 * @property {Object} logoZone - Logo zone constraints
 * @property {Object} logoZone.position - Logo position {x, y}
 * @property {Object} logoZone.maxSize - Max logo size {width, height}
 * @property {string[]} logoZone.colorModes - Supported color modes
 * @property {Object} [buttonZone] - Button zone constraints
 * @property {'dark' | 'light' | 'adaptive'} backgroundStyle - Preferred background style
 */

/**
 * Template constraints library
 * @type {TemplateConstraints[]}
 */
export const TEMPLATE_CONSTRAINTS = [
  {
    id: "polotno-christmas-template",
    name: "Merry Christmas",
    size: "SQUARE",
    textZones: {
      heading: {
        maxLines: 2,
        maxCharsPerLine: 20,
        fontSizeRange: { min: 120, max: 120 },
        position: { x: 100, y: 296, width: 525, height: 263 },
        alignment: "center",
        allowedStyles: ["script-font"]
      },
      subheading: {
        maxLines: 3,
        maxCharsPerLine: 40,
        fontSizeRange: { min: 22, max: 22 },
        position: { x: 274, y: 528, width: 532, height: 37 },
        alignment: "center",
        allowedStyles: ["uppercase", "letter-spacing"]
      }
    },
    logoZone: {
      position: { x: 440, y: 20 },
      maxSize: { width: 200, height: 200 },
      colorModes: ["white", "black", "color"]
    },
    backgroundStyle: "adaptive"
  },
  {
    id: "polotno-traveler-template",
    name: "Traveler",
    size: "SQUARE",
    textZones: {
      heading: {
        maxLines: 1,
        maxCharsPerLine: 10,
        fontSizeRange: { min: 160, max: 160 },
        position: { x: 0, y: 682, width: 1082, height: 347 },
        alignment: "center",
        allowedStyles: ["script-font"]
      },
      subheading: {
        maxLines: 1,
        maxCharsPerLine: 20,
        fontSizeRange: { min: 60, max: 60 },
        position: { x: 251, y: 664, width: 758, height: 141 },
        alignment: "justify",
        allowedStyles: ["script-font"]
      },
      cta: {
        maxLines: 1,
        maxCharsPerLine: 10,
        fontSizeRange: { min: 70, max: 70 },
        position: { x: 203, y: 912, width: 716, height: 87 },
        alignment: "right",
        allowedStyles: ["handwritten"]
      }
    },
    logoZone: {
      position: { x: 50, y: 50 },
      maxSize: { width: 150, height: 150 },
      colorModes: ["white", "black", "color"]
    },
    backgroundStyle: "adaptive"
  },
  {
    id: "polotno-valentine-template",
    name: "Valentine Day Sale",
    size: "SQUARE",
    textZones: {
      heading: {
        maxLines: 2,
        maxCharsPerLine: 14,
        fontSizeRange: { min: 100, max: 100 },
        position: { x: 325, y: 640, width: 675, height: 241 },
        alignment: "right",
        allowedStyles: ["uppercase"]
      },
      subheading: {
        maxLines: 1,
        maxCharsPerLine: 15,
        fontSizeRange: { min: 45, max: 45 },
        position: { x: 396, y: 896, width: 604, height: 78 },
        alignment: "center",
        allowedStyles: ["uppercase"]
      }
    },
    logoZone: {
      position: { x: 50, y: 58 },
      maxSize: { width: 173, height: 165 },
      colorModes: ["white", "color"]
    },
    decorativeElements: {
      // Brand color accent triangle (id: Jqiz2kC-eI)
      triangleAccent: {
        id: "Jqiz2kC-eI",
        useBrandColor: true
      }
    },
    backgroundStyle: "dark" // Text is white, designed for dark backgrounds
  },
  {
    id: "hiring-template",
    name: "Now Hiring",
    size: "SQUARE",
    textZones: {
      heading: {
        maxLines: 2,
        maxCharsPerLine: 12,
        fontSizeRange: { min: 178, max: 178 },
        position: { x: 107, y: 307, width: 866, height: 357 },
        alignment: "center",
        allowedStyles: ["bold", "uppercase"]
      },
      cta: {
        maxLines: 1,
        maxCharsPerLine: 25,
        fontSizeRange: { min: 49, max: 49 },
        position: { x: 227, y: 760, width: 627, height: 60 },
        alignment: "center",
        allowedStyles: ["uppercase"]
      }
    },
    logoZone: {
      position: { x: 465, y: 60 },
      maxSize: { width: 150, height: 150 },
      colorModes: ["white", "black", "color"]
    },
    backgroundStyle: "dark"
  },
  {
    id: "operations-manager-left-template",
    name: "Operations Manager (Left)",
    size: "SQUARE",
    textZones: {
      subheading: {
        maxLines: 1,
        maxCharsPerLine: 20,
        fontSizeRange: { min: 40, max: 40 },
        position: { x: 76, y: 282, width: 350, height: 49 },
        alignment: "left",
        allowedStyles: ["bold", "uppercase"]
      },
      heading: {
        maxLines: 3,
        maxCharsPerLine: 13,
        fontSizeRange: { min: 124, max: 124 },
        position: { x: 72, y: 331, width: 639, height: 299 },
        alignment: "left",
        allowedStyles: ["bold", "uppercase"]
      },
      description: {
        maxLines: 4,
        maxCharsPerLine: 35,
        fontSizeRange: { min: 39, max: 39 },
        position: { x: 72, y: 630, width: 639, height: 165 },
        alignment: "left",
        allowedStyles: []
      },
      cta: {
        maxLines: 1,
        maxCharsPerLine: 25,
        fontSizeRange: { min: 36, max: 36 },
        position: { x: 80, y: 892, width: 439, height: 45 },
        alignment: "center",
        allowedStyles: ["uppercase"]
      }
    },
    logoZone: {
      position: { x: 76, y: 82 },
      maxSize: { width: 164, height: 175 },
      colorModes: ["white", "color"]
    },
    backgroundStyle: "dark"
  },
  {
    id: "operations-manager-center-template",
    name: "Operations Manager (Center)",
    size: "SQUARE",
    textZones: {
      heading: {
        maxLines: 3,
        maxCharsPerLine: 13,
        fontSizeRange: { min: 124, max: 124 },
        position: { x: 221, y: 391, width: 639, height: 299 },
        alignment: "center",
        allowedStyles: ["bold", "uppercase"]
      },
      cta: {
        maxLines: 1,
        maxCharsPerLine: 25,
        fontSizeRange: { min: 36, max: 36 },
        position: { x: 321, y: 845, width: 439, height: 45 },
        alignment: "center",
        allowedStyles: ["uppercase"]
      }
    },
    logoZone: {
      position: { x: 426, y: 95 },
      maxSize: { width: 227, height: 238 },
      colorModes: ["white", "color"]
    },
    backgroundStyle: "dark"
  },
  {
    id: "landscape-promo-1",
    name: "Landscape Promo 1",
    size: "HORIZONTAL",
    textZones: {
      heading: {
        maxLines: 2,
        maxCharsPerLine: 16,
        fontSizeRange: { min: 70, max: 74 },
        position: { x: 62, y: 196, width: 571, height: 174 },
        alignment: "left",
        allowedStyles: ["bold"]
      },
      description: {
        maxLines: 1,
        maxCharsPerLine: 30,
        fontSizeRange: { min: 22, max: 24 },
        position: { x: 62, y: 394, width: 373, height: 33 },
        alignment: "left",
        allowedStyles: []
      },
      cta: {
        maxLines: 1,
        maxCharsPerLine: 12,
        fontSizeRange: { min: 21, max: 21 },
        position: { x: 64, y: 494, width: 256, height: 27 },
        alignment: "center",
        allowedStyles: []
      }
    },
    logoZone: {
      position: { x: 64, y: 74 },
      maxSize: { width: 192, height: 96 },
      colorModes: ["white", "black", "color"]
    },
    backgroundStyle: "dark"
  },
  {
    id: "landscape-promo-2",
    name: "Landscape Promo 2",
    size: "HORIZONTAL",
    textZones: {
      heading: {
        maxLines: 1,
        maxCharsPerLine: 4,
        fontSizeRange: { min: 225, max: 225 },
        position: { x: 10, y: 301, width: 600, height: 271 },
        alignment: "center",
        allowedStyles: []
      },
      subheading: {
        maxLines: 2,
        maxCharsPerLine: 12,
        fontSizeRange: { min: 54, max: 58 },
        position: { x: 76, y: 249, width: 388, height: 69 },
        alignment: "center",
        allowedStyles: []
      },
      description: {
        maxLines: 2,
        maxCharsPerLine: 22,
        fontSizeRange: { min: 22.75, max: 22.75 },
        position: { x: 76, y: 536, width: 483, height: 51 },
        alignment: "left",
        allowedStyles: []
      },
      cta: {
        maxLines: 1,
        maxCharsPerLine: 13,
        fontSizeRange: { min: 41, max: 41 },
        position: { x: 909, y: 577, width: 297, height: 51 },
        alignment: "center",
        allowedStyles: []
      }
    },
    logoZone: {
      position: { x: 76, y: 40 },
      maxSize: { width: 360, height: 160 },
      colorModes: ["white", "black", "color"]
    },
    backgroundStyle: "dark"
  },
  {
    id: "landscape-3-promo",
    name: "Landscape 3 Promo",
    size: "HORIZONTAL",
    textZones: {
      heading: {
        maxLines: 3,
        maxCharsPerLine: 10,
        fontSizeRange: { min: 109, max: 109 },
        position: { x: 60, y: 262, width: 659, height: 394 },
        alignment: "left",
        allowedStyles: ["uppercase"]
      }
    },
    logoZone: {
      position: { x: 70, y: 73 },
      maxSize: { width: 356, height: 159 },
      colorModes: ["white", "black", "color"]
    },
    backgroundStyle: "dark"
  },
  {
    id: "landscape-4-promo",
    name: "Landscape 4 Promo",
    size: "HORIZONTAL",
    textZones: {
      heading: {
        maxLines: 1,
        maxCharsPerLine: 10,
        fontSizeRange: { min: 110, max: 120 },
        position: { x: 532, y: 230, width: 640, height: 277 },
        alignment: "center",
        allowedStyles: ["uppercase"]
      },
      subheading: {
        maxLines: 1,
        maxCharsPerLine: 15,
        fontSizeRange: { min: 80, max: 80 },
        position: { x: 549, y: 541, width: 627, height: 97 },
        alignment: "right",
        allowedStyles: []
      }
    },
    logoZone: {
      position: { x: 50, y: 49 },
      maxSize: { width: 148, height: 132 },
      colorModes: ["white", "black", "color"]
    },
    backgroundStyle: "dark"
  },
  {
    id: "portrait-1-promo",
    name: "Portrait 1 Promo",
    size: "VERTICAL",
    textZones: {
      heading: {
        maxLines: 4,
        maxCharsPerLine: 9,
        fontSizeRange: { min: 124, max: 124 },
        position: { x: 104, y: 566, width: 634, height: 597 },
        alignment: "left",
        allowedStyles: ["bold"]
      },
      description: {
        maxLines: 2,
        maxCharsPerLine: 16,
        fontSizeRange: { min: 59, max: 59 },
        position: { x: 109, y: 1212, width: 536, height: 167 },
        alignment: "left",
        allowedStyles: []
      },
      cta: {
        maxLines: 1,
        maxCharsPerLine: 22,
        fontSizeRange: { min: 43, max: 43 },
        position: { x: 109, y: 1606, width: 531, height: 53 },
        alignment: "center",
        allowedStyles: []
      }
    },
    logoZone: {
      position: { x: 109, y: 325 },
      maxSize: { width: 199, height: 199 },
      colorModes: ["white", "black", "color"]
    },
    backgroundStyle: "dark"
  },
  {
    id: "portrait-2-promo",
    name: "Portrait 2 Promo",
    size: "VERTICAL",
    textZones: {
      heading: {
        maxLines: 3,
        maxCharsPerLine: 9,
        fontSizeRange: { min: 161, max: 161 },
        position: { x: 131, y: 647, width: 818, height: 485 },
        alignment: "center",
        allowedStyles: []
      },
      cta: {
        maxLines: 1,
        maxCharsPerLine: 16,
        fontSizeRange: { min: 41, max: 41 },
        position: { x: 217, y: 1611, width: 646, height: 51 },
        alignment: "center",
        allowedStyles: ["uppercase"]
      }
    },
    logoZone: {
      position: { x: 360, y: 90 },
      maxSize: { width: 359, height: 320 },
      colorModes: ["white", "black", "color"]
    },
    backgroundStyle: "dark"
  },
  {
    id: "portrait-3-promo",
    name: "Portrait 3 Promo",
    size: "VERTICAL",
    textZones: {
      heading: {
        maxLines: 3,
        maxCharsPerLine: 10,
        fontSizeRange: { min: 140, max: 145 },
        position: { x: 213, y: 704, width: 688, height: 513 },
        alignment: "center",
        allowedStyles: ["uppercase"]
      },
      subheading: {
        maxLines: 2,
        maxCharsPerLine: 15,
        fontSizeRange: { min: 60, max: 60 },
        position: { x: 260, y: 1316, width: 560, height: 145 },
        alignment: "center",
        allowedStyles: []
      },
      cta: {
        maxLines: 1,
        maxCharsPerLine: 16,
        fontSizeRange: { min: 60, max: 60 },
        position: { x: 277, y: 1615, width: 560, height: 73 },
        alignment: "center",
        allowedStyles: ["uppercase"]
      }
    },
    logoZone: {
      position: { x: 0, y: 0 },
      maxSize: { width: 0, height: 0 },
      colorModes: []
    },
    backgroundStyle: "dark"
  },
  {
    id: "portrait-4-promo",
    name: "Portrait 4 Promo",
    size: "VERTICAL",
    textZones: {
      heading: {
        maxLines: 2,
        maxCharsPerLine: 14,
        fontSizeRange: { min: 85, max: 90 },
        position: { x: 260, y: 830, width: 585, height: 210 },
        alignment: "center",
        allowedStyles: []
      },
      subheading: {
        maxLines: 1,
        maxCharsPerLine: 18,
        fontSizeRange: { min: 55, max: 55 },
        position: { x: 271, y: 1354, width: 561, height: 133 },
        alignment: "center",
        allowedStyles: []
      }
    },
    logoZone: {
      position: { x: 386, y: 300 },
      maxSize: { width: 307, height: 273 },
      colorModes: ["white", "black", "color"]
    },
    backgroundStyle: "light"
  }
];

/**
 * Get constraints for a specific template
 * @param {string} templateId - Template ID
 * @returns {TemplateConstraints | null}
 */
export function getTemplateConstraints(templateId) {
  return TEMPLATE_CONSTRAINTS.find((t) => t.id === templateId) || null;
}

/**
 * Check if template has constraints defined
 * @param {string} templateId - Template ID
 * @returns {boolean}
 */
export function hasConstraints(templateId) {
  return TEMPLATE_CONSTRAINTS.some((t) => t.id === templateId);
}

/**
 * Get all templates with constraints
 * @returns {string[]} Array of template IDs
 */
export function getConstrainedTemplateIds() {
  return TEMPLATE_CONSTRAINTS.map((t) => t.id);
}
